// Small pieces the four Evals pages and the case drawer share: the gate and
// status badges, a figure with its interval, the case markers and the
// per-repeat marks. Styles: styles/admin-evals.css.

import { Link } from 'react-router-dom';

import {
  caseMarkers,
  caseOutcome,
  decisionSummary,
  outcomeText,
  repeatMark,
  repeatMarkText,
  repeatSummary,
  sortedRepeats,
} from './caseTable';
import {
  formatFigure,
  formatInterval,
  gateLabel,
  gateReasonText,
  gateTone,
  shortId,
  statusLabel,
  statusTone,
} from './format';
import { runHref } from './query';
import type { EvalCaseRow, EvalFigure, EvalGate, EvalGateReason, EvalRunStatus } from './types';

export function EvalBadge({
  tone,
  children,
  title,
  testId,
}: {
  tone: 'succeeded' | 'failed' | 'running' | 'neutral' | 'warn' | 'bad' | 'muted';
  children: React.ReactNode;
  title?: string;
  testId?: string;
}) {
  return (
    <span className={`eval-badge eval-badge--${tone}`} title={title} data-testid={testId}>
      {children}
    </span>
  );
}

export function GateBadge({
  gate,
  reason,
}: {
  gate: EvalGate | null;
  reason: EvalGateReason | null;
}) {
  const why = gateReasonText(reason);
  return (
    <EvalBadge tone={gateTone(gate)} title={why.length > 0 ? why : undefined}>
      {gateLabel(gate)}
    </EvalBadge>
  );
}

export function StatusPill({ status, stalled }: { status: EvalRunStatus; stalled?: boolean }) {
  return (
    <>
      <EvalBadge tone={statusTone(status)}>{statusLabel(status)}</EvalBadge>
      {stalled === true ? (
        <EvalBadge tone="warn" title="No progress within the stall window (ADR 0232 §3)">
          Stalled
        </EvalBadge>
      ) : null}
    </>
  );
}

/** "11/11 · 100%" with its interval beneath when the run has one. */
export function FigureCell({
  figure,
  repeats,
}: {
  figure: EvalFigure | null | undefined;
  repeats: number;
}) {
  const text = formatFigure(figure);
  if (figure === null || figure === undefined || text === '—') {
    return <span className="admin-muted">—</span>;
  }
  const interval = repeats > 1 ? formatInterval(figure.ci95) : '';
  return (
    <span className="eval-figure">
      <span>{text}</span>
      {interval.length > 0 ? <span className="eval-figure-ci">{interval}</span> : null}
    </span>
  );
}

export function CaseMarkerChips({ row }: { row: EvalCaseRow }) {
  const markers = caseMarkers(row);
  if (markers.length === 0) return null;
  return (
    <span className="eval-markers">
      {markers.map((m) => (
        <EvalBadge key={m.key} tone={m.tone} title={m.title} testId={`eval-marker-${m.key}`}>
          {m.label}
        </EvalBadge>
      ))}
    </span>
  );
}

/**
 * A case's outcome as the run counts it, its per-repeat marks and the repeats
 * it read. A Watch condition also says how its decision went: a condition met
 * whose decision did not match is a failure, as the failures filter and
 * compare count it.
 */
export function CaseOutcome({ row }: { row: EvalCaseRow }) {
  const outcome = caseOutcome(row);
  const decision = decisionSummary(row);
  const decisionFailed = row.outcome !== 'fail' && row.decisionOutcome === 'fail';
  return (
    <span className="eval-stack">
      <span className="eval-nowrap">
        <EvalBadge
          tone={outcome === 'pass' ? 'succeeded' : outcome === 'fail' ? 'failed' : 'neutral'}
          title={
            decisionFailed
              ? 'The condition was met, but the Watch decision did not match.'
              : undefined
          }
        >
          {outcomeText(outcome)}
        </EvalBadge>{' '}
        <RepeatMarks row={row} />
      </span>
      <span className="admin-muted eval-sub">{repeatSummary(row)}</span>
      {decision !== null ? (
        <span
          className={`eval-sub ${row.decisionOutcome === 'fail' ? 'eval-error-text' : 'admin-muted'}`}
          data-testid="eval-case-decision"
        >
          {decision}
        </span>
      ) : null}
    </span>
  );
}

/** One mark per repeat, in repeat order: pass, fail, error, not graded, blocked. */
export function RepeatMarks({ row }: { row: EvalCaseRow }) {
  if (row.repeats.length === 0) return <span className="admin-muted">—</span>;
  const repeats = sortedRepeats(row);
  return (
    <span
      className="eval-repeats"
      aria-label={repeats
        .map((r) => `repeat ${r.repeat} ${repeatMarkText(repeatMark(r))}`)
        .join(', ')}
    >
      {repeats.map((r) => {
        const mark = repeatMark(r);
        return (
          <span
            key={r.resultId}
            className={`eval-repeat eval-repeat--${mark}`}
            title={`Repeat ${r.repeat}: ${repeatMarkText(mark)}`}
          />
        );
      })}
    </span>
  );
}

export function RunLink({ id }: { id: string }) {
  return (
    <Link to={runHref(id)} title={id}>
      <code>{shortId(id)}</code>
    </Link>
  );
}

/** The error banner every Evals page shows when a read fails. */
export function EvalError({ message }: { message: string | null }) {
  if (message === null) return null;
  return (
    <div className="admin-auth-banner error" role="alert">
      {message}
    </div>
  );
}
